import { ArrowRight, BookOpen, GraduationCap, Award, Users, Smartphone, Text, MessageCircle, RefreshCcw, Phone, X } from 'lucide-react';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import '../test-banner.css';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedReviewWidget from '../components/AnimatedReviewWidget';
import { useSEO } from '../hooks/useSEO';

const HomePage: React.FC = () => {
  if (process.env.NODE_ENV === 'development') {
    console.log('HOMEPAGE: HomePage component render');
  }
  // Enable SEO for homepage
  useSEO();
  
  // Memoize static data to prevent re-creation on every render
  const features = useMemo(() => [
    {
      icon: <BookOpen className="h-8 w-8" />,
      title: "MBBS Guidance",
      description: "Complete guidance for medical admissions in India and abroad",
      link: "/mbbs"
    },
    {
      icon: <GraduationCap className="h-8 w-8" />,
      title: "Engineering Courses",
      description: "All engineering branches and top colleges information",
      link: "/engineering"
    },

    {
      icon: <Users className="h-8 w-8" />,
      title: "Expert Guidance",
      description: "Personalized counseling from education experts",
      link: "/services"
    },
    {
      icon: <Award className="h-8 w-8" />,
      title: "Placements & Training",
      description: "Career development and job placement support for graduates",
      link: "/services"
    }
  ], []);

  const stats = useMemo(() => [
    { number: "50,000+", label: "Students Guided" },
    { number: "500+", label: "Partner Colleges" },
    { number: "20+", label: "Branches in Tamil Nadu" },
    { number: "95%", label: "Success Rate" },
    { number: "20+", label: "Years Experience" },
    { number: "50+", label: "Global Partners" },
    { number: "6000+", label: "Abroad Clients"},
    { number: "50+" , label: "PhD Experts" }
  ], []);

  // Optimized animation variants
  const fadeInUp = useMemo(() => ({
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3 }
  }), []);

  const staggerContainer = useMemo(() => ({
    animate: {
      transition: {
        staggerChildren: 0.05
      }
    }
  }), []);

  // Tray Toolbar State
  const [showNumbers, setShowNumbers] = useState(false);
  const [fontSize, setFontSize] = useState(1);
  const [mobileView, setMobileView] = useState(false);
  const [trayVisible, setTrayVisible] = useState(false);
  
  // Ref for phone numbers popup
  const phonePopupRef = useRef<HTMLDivElement>(null);

  // Auto-hide tray after 5 seconds of inactivity
  useEffect(() => {
    let timeout: number;
    if (trayVisible) {
      timeout = window.setTimeout(() => {
        setTrayVisible(false);
      }, 5000); // Auto-hide after 5 seconds
    }
    return () => window.clearTimeout(timeout);
  }, [trayVisible]);

  // Close phone popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (phonePopupRef.current && !phonePopupRef.current.contains(event.target as Node)) {
        setShowNumbers(false);
      }
    };

    if (showNumbers) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNumbers]);

  // Font size classes
  const fontSizeClass = useMemo(() => {
    return fontSize === 1 ? '' : fontSize === 2 ? 'text-[1.1rem] md:text-[1.15rem]' : 'text-[1.25rem] md:text-[1.3rem]';
  }, [fontSize]);

  // Reset UI
  const resetUI = () => {
    setFontSize(1);
    setMobileView(false);
  };

  // Memoized TrayIcon component to prevent re-renders
  const TrayIcon = useMemo(() => ({ icon, tooltip, onClick }: { icon: React.ReactNode; tooltip: string; onClick: () => void }) => (
    <button
      className="group w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl shadow-lg transition-all duration-200 hover:scale-110 hover:ring-2 hover:ring-purple-400 focus:outline-none relative"
      onClick={onClick}
      aria-label={tooltip}
      tabIndex={0}
    >
      {icon}
      <span className="absolute left-12 md:left-14 top-1/2 -translate-y-1/2 bg-black/80 text-white text-xs px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
        {tooltip}
      </span>
    </button>
  ), []);

  return (
    <React.Fragment>
  {/* test banner removed */}
      <div 
        className={`min-h-screen relative font-[Inter,Poppins,sans-serif] antialiased bg-gradient-to-br from-slate-900 via-blue-900 to-black text-white ${fontSizeClass} transition-all duration-300`}
      >
        {/* Tray Toolbar with Toggle */}
        <div className="fixed left-2 md:left-3 top-1/2 transform -translate-y-1/2 z-50 flex items-center">
          {/* Toggle Button */}
          <button
            className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-gradient-to-r from-cyan-400/80 to-pink-400/80 backdrop-blur-lg rounded-full shadow-xl hover:scale-110 transition-all duration-200 hover:ring-2 hover:ring-purple-400 focus:outline-none"
            onClick={() => setTrayVisible(!trayVisible)}
            aria-label={trayVisible ? 'Hide Tray' : 'Show Tray'}
          >
            <ArrowRight className={`h-4 w-4 md:h-5 md:w-5 text-white transition-transform duration-200 ${trayVisible ? 'rotate-180' : ''}`} />
          </button>

          {/* Tray Panel */}
          <div className={`ml-1 md:ml-2 backdrop-blur-lg bg-white/10 p-1 md:p-2 rounded-xl space-y-2 md:space-y-3 shadow-xl flex flex-col items-center transition-all duration-200 ${
            trayVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 pointer-events-none'
          }`}>
            <TrayIcon
              icon={<Smartphone className={`h-5 w-5 md:h-6 md:w-6 text-cyan-400 group-hover:text-pink-400 drop-shadow-glow`} />}
              tooltip={mobileView ? 'Desktop View' : 'Toggle Mobile View'}
              onClick={() => setMobileView((v) => !v)}
            />
            <TrayIcon
              icon={<Text className={`h-5 w-5 md:h-6 md:w-6 text-pink-400 group-hover:text-cyan-400 drop-shadow-glow`} />}
              tooltip="Font Size +"
              onClick={() => setFontSize((f) => Math.min(f + 1, 3))}
            />
            <TrayIcon
              icon={<Text className={`h-5 w-5 md:h-6 md:w-6 text-cyan-400 group-hover:text-pink-400 drop-shadow-glow rotate-180`} />}
              tooltip="Font Size -"
              onClick={() => setFontSize((f) => Math.max(f - 1, 1))}
            />
            <TrayIcon
              icon={<Phone className={`h-5 w-5 md:h-6 md:w-6 text-green-400 group-hover:text-cyan-400 drop-shadow-glow`} />}
              tooltip="Show Numbers"
              onClick={() => setShowNumbers((v) => !v)}
            />
            <TrayIcon
              icon={<MessageCircle className={`h-5 w-5 md:h-6 md:w-6 text-cyan-400 group-hover:text-pink-400 drop-shadow-glow`} />}
              tooltip="WhatsApp Chat"
              onClick={() => window.open('https://wa.me/7708677132', '_blank')}
            />
            <TrayIcon
              icon={<RefreshCcw className={`h-5 w-5 md:h-6 md:w-6 text-purple-400 group-hover:text-pink-400 drop-shadow-glow`} />}
              tooltip="Reset UI"
              onClick={resetUI}
            />
          </div>

          {/* Phone Numbers Popup */}
          {showNumbers && (
            <div 
              ref={phonePopupRef}
              className="fixed top-1/2 left-12 md:left-16 -translate-y-1/2 bg-black/90 border-2 border-green-400 rounded-2xl p-3 md:p-5 text-white shadow-2xl text-sm md:text-base space-y-3 md:space-y-4 w-64 md:w-72 z-[200] flex flex-col items-start animate-fade-in"
            >
              {/* Close Button */}
              <button
                onClick={() => setShowNumbers(false)}
                className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-red-500/80 hover:bg-red-500 rounded-full text-white text-xs transition-colors"
                aria-label="Close"
              >
                <X className="h-3 w-3" />
              </button>
              
              <button
                onClick={() => window.open('tel:+919361027132', '_self')}
                className="flex items-center space-x-2 md:space-x-3 w-full p-2 rounded-lg hover:bg-green-500/20 transition-colors cursor-pointer group"
              >
                <Phone className="h-4 w-4 md:h-5 md:w-5 text-green-400 group-hover:text-green-300" />
                <span className="font-bold text-xs md:text-sm">Advisor:</span>
                <span className="text-xs md:text-sm group-hover:text-green-300">+91 93610 27132</span>
              </button>
              
              <button
                onClick={() => window.open('tel:+916382167899', '_self')}
                className="flex items-center space-x-2 md:space-x-3 w-full p-2 rounded-lg hover:bg-green-500/20 transition-colors cursor-pointer group"
              >
                <Phone className="h-4 w-4 md:h-5 md:w-5 text-green-400 group-hover:text-green-300" />
                <span className="font-bold text-xs md:text-sm">Admission:</span>
                <span className="text-xs md:text-sm group-hover:text-green-300">+91 63821 67899</span>
              </button>
              
              <button
                onClick={() => window.open('tel:+917708677132', '_self')}
                className="flex items-center space-x-2 md:space-x-3 w-full p-2 rounded-lg hover:bg-green-500/20 transition-colors cursor-pointer group"
              >
                <Phone className="h-4 w-4 md:h-5 md:w-5 text-green-400 group-hover:text-green-300" />
                <span className="font-bold text-xs md:text-sm">Enquiry:</span>
                <span className="text-xs md:text-sm group-hover:text-green-300">+91 77086 77132</span>
              </button>
            </div>
          )}
        </div>

        {/* Main Content Wrapper for Mobile Preview */}
        <div className={mobileView ? 'max-w-xs mx-auto border-2 border-blue-400 rounded-2xl shadow-2xl bg-slate-800/80 p-2' : ''}>
          {/* Hero Section */}
          <section className="relative overflow-hidden">
            {/* Simplified Background Elements */}
            <div className="absolute inset-0">
              {/* Static Gradient Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-black" />
              
              {/* Simple Floating Orbs - Reduced */}
              <motion.div 
                className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-blue-600/10 rounded-full blur-xl"
                animate={{
                  y: [0, -20, 0],
                  opacity: [0.3, 0.5, 0.3]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              <motion.div 
                className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-indigo-400/10 to-blue-500/10 rounded-full blur-xl"
                animate={{
                  y: [0, 20, 0],
                  opacity: [0.2, 0.4, 0.2]
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              />

              {/* Simple Grid Pattern */}
              <div
                className="absolute inset-0 opacity-5 grid-bg"
              />

              {/* Static Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-indigo-500/5" />
            </div>

            {/* Dark overlay for better text contrast */}
            <div className="absolute inset-0 bg-black/30 rounded-b-3xl"></div>
            
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 z-10">
              <div className="text-center">
                <h1 className="text-4xl md:text-6xl font-extrabold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-blue-300 tracking-tight">
                  Your Future Starts Here
                </h1>
                <p className="text-xl md:text-2xl mb-8 text-slate-300">
                  Expert guidance for Medical, Engineering, and Scholarship opportunities
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/consultancy#consultation-form"
                    className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-8 py-3 rounded-lg font-semibold hover:scale-105 hover:shadow-lg transition inline-flex items-center justify-center relative overflow-hidden group"
                  >
                    <span className="relative z-10">Get Free Guidance</span>
                    <ArrowRight className="ml-2 h-5 w-5 relative z-10" />
                  </Link>
                </div>
              </div>
            </div>
          </section>

        {/* Features Section */}
        <section className="py-16 bg-slate-900 relative overflow-hidden">
          {/* Simplified Features Section Background */}
          <div className="absolute inset-0">
            {/* Static Code Blocks */}
            <div className="absolute top-20 left-10 w-16 h-16 border border-green-400/10 rounded-lg opacity-30">
              <div className="w-2 h-2 bg-green-400/30 rounded-full absolute top-2 left-2" />
              <div className="w-1 h-6 bg-green-400/20 absolute top-6 left-2" />
              <div className="w-4 h-1 bg-green-400/20 absolute top-10 left-2" />
            </div>
            <div className="absolute bottom-20 right-20 w-12 h-12 border border-cyan-400/10 rounded-lg opacity-20">
              <div className="w-2 h-2 bg-cyan-400/30 rounded-full absolute top-2 left-2" />
            </div>

            {/* Simple Network Lines */}
            <svg className="absolute inset-0 w-full h-full opacity-5" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10,50 Q200,20 400,80 T800,60"
                stroke="#22d3ee"
                strokeWidth="1"
                fill="none"
                strokeDasharray="10,5"
              />
            </svg>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-blue-300 tracking-tight">
                Why Choose SEWA?
              </h2>
              <p className="text-lg max-w-2xl mx-auto text-slate-400">
                We provide comprehensive educational guidance to help you make informed decisions about your academic future
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-800/30 rounded-xl shadow-lg p-6 hover:scale-105 hover:shadow-xl transition-all duration-200 cursor-pointer group relative overflow-hidden"
                  {...fadeInUp}
                  transition={{ ...fadeInUp.transition, delay: index * 0.05 }}
                  whileHover={{ 
                    scale: 1.05,
                    transition: { duration: 0.15 }
                  }}
                >
                  {/* Card Hover Effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 rounded-xl opacity-0 group-hover:opacity-100"
                    transition={{ duration: 0.2 }}
                  />
                  
                  {/* Simplified Icon Animation */}
                  <motion.div 
                    className="mb-4 text-blue-400 group-hover:text-indigo-400 transition-colors duration-200 relative z-10"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    {feature.icon}
                  </motion.div>
                  
                  <h3 className="text-xl font-semibold mb-2 text-white relative z-10">
                    {feature.title}
                  </h3>
                  <p className="text-slate-300 mb-4 relative z-10">
                    {feature.description}
                  </p>
                  
                  {/* Simple Learn More Link */}
                  <Link
                    to={feature.link}
                    className="text-blue-400 font-medium hover:text-indigo-400 inline-flex items-center transition-colors duration-200 relative z-10 group/link"
                  >
                    Learn More
                    <ArrowRight className="ml-1 h-4 w-4 group-hover/link:translate-x-1 transition-transform duration-200" />
                  </Link>

                  {/* Card Corner Accent */}
                  <motion.div
                    className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-blue-500/20 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100"
                    transition={{ duration: 0.2 }}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-gradient-to-r from-slate-800 to-blue-900 relative overflow-hidden">
          {/* Simplified Stats Section Background */}
          <div className="absolute inset-0">
            {/* Static Chart Elements */}
            <div className="absolute left-20 bottom-20 w-8 h-16 bg-gradient-to-t from-blue-500/10 to-indigo-400/20 rounded-t-md opacity-30" />
            <div className="absolute left-32 bottom-20 w-8 h-20 bg-gradient-to-t from-blue-500/10 to-indigo-400/20 rounded-t-md opacity-25" />
            <div className="absolute left-44 bottom-20 w-8 h-12 bg-gradient-to-t from-blue-500/10 to-indigo-400/20 rounded-t-md opacity-35" />

            {/* Static Progress Ring */}
            <div className="absolute top-1/2 right-1/4 w-32 h-32 -translate-y-1/2 opacity-20">
              <svg className="w-full h-full" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  stroke="#3b82f6"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="251.2"
                  strokeDashoffset="62.8"
                  opacity="0.4"
                />
              </svg>
            </div>

            {/* Static Success Arrow */}
            <div className="absolute bottom-1/4 left-1/3 w-16 h-16 opacity-30">
              <div className="w-full h-1 bg-gradient-to-r from-emerald-400/50 to-green-500/50 absolute top-1/2 -translate-y-1/2" />
              <div className="w-4 h-4 border-t-2 border-r-2 border-green-400/50 rotate-45 absolute right-0 top-1/2 -translate-y-1/2 -translate-x-1" />
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div 
              className="grid grid-cols-2 lg:grid-cols-4 gap-8"
              {...staggerContainer}
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center bg-slate-800/60 backdrop-blur-sm border border-blue-800/30 rounded-xl shadow-lg p-6 hover:scale-105 hover:shadow-xl transition-all duration-200 relative overflow-hidden group"
                  {...fadeInUp}
                  transition={{ ...fadeInUp.transition, delay: index * 0.05 }}
                  whileHover={{ 
                    scale: 1.05,
                    transition: { duration: 0.15 }
                  }}
                >
                  {/* Card Glow Effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-indigo-500/5 rounded-xl opacity-0 group-hover:opacity-100"
                    transition={{ duration: 0.2 }}
                  />
                  
                  {/* Simple Number Display */}
                  <div className="text-3xl md:text-4xl font-bold text-blue-400 mb-2 relative z-10">
                    {stat.number}
                  </div>
                  
                  <div className="text-slate-300 relative z-10">
                    {stat.label}
                  </div>

                  {/* Simple Border */}
                  <div className="absolute inset-0 border border-blue-400/0 group-hover:border-blue-400/30 rounded-xl transition-colors duration-200" />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-slate-900 relative overflow-hidden">
          {/* Simplified CTA Section Background */}
          <div className="absolute inset-0">
            {/* Static Success Path */}
            <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M100,400 Q300,200 500,300 T900,200"
                stroke="#10b981"
                strokeWidth="2"
                fill="none"
                strokeDasharray="20,10"
                opacity="0.3"
              />
              {/* Static Success Checkpoints */}
              <circle cx="30%" cy="50%" r="6" fill="#10b981" opacity="0.4" />
              <circle cx="50%" cy="50%" r="6" fill="#10b981" opacity="0.4" />
              <circle cx="70%" cy="50%" r="6" fill="#10b981" opacity="0.4" />
            </svg>

            {/* Static Goal Target */}
            <div className="absolute top-1/4 right-1/6 w-24 h-24 opacity-20">
              <div className="absolute inset-0 border-2 border-green-400/30 rounded-full" />
              <div className="absolute inset-2 border border-green-400/50 rounded-full" />
              <div className="absolute inset-4 border border-green-400/70 rounded-full" />
              <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-green-400 rounded-full -translate-x-1/2 -translate-y-1/2" />
            </div>

            {/* Static Achievement Badges */}
            <div className="absolute top-30 left-20 w-6 h-6 bg-gradient-to-br from-yellow-400/10 to-orange-500/10 rounded-full border border-yellow-400/20 opacity-30">
              <div className="w-1 h-1 bg-yellow-400/40 rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <div className="absolute top-50 right-32 w-8 h-8 bg-gradient-to-br from-yellow-400/10 to-orange-500/10 rounded-full border border-yellow-400/20 opacity-25">
              <div className="w-1 h-1 bg-yellow-400/40 rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>

            {/* Static Gradient Wave */}
            <div className="absolute bottom-0 left-0 w-full h-20 bg-gradient-to-t from-blue-600/5 to-transparent" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-blue-300 tracking-tight">
                Ready to Start Your Journey?
              </h2>
              <p className="text-xl mb-8 text-slate-300">
                Get personalized guidance from our education experts today
              </p>
              <Link
                to="/consultancy#consultation-form"
                className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-8 py-3 rounded-lg font-semibold hover:scale-105 hover:shadow-lg transition inline-flex items-center"
              >
                Book Free Guidance
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Notification bar for updates */}
        {/* <NotificationBar /> */}

        {/* Animated Review Widget */}
        <AnimatedReviewWidget />
      </div>
    </div>
    </React.Fragment>
  );
};

export default HomePage;